import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, RefreshCw, Trash2, ShieldCheck, UserPlus, X, Copy, Loader2, UserMinus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Select } from "@/components/FormBits";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [tempPwReveal, setTempPwReveal] = useState(null); // { email, temp_password }

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A new temporary password will be emailed to them.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPwReveal({ email: u.email, temp_password: data.temp_password });
      fetchUsers();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const toggleDisable = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Account enabled" : "Account disabled");
      fetchUsers();
    } catch (e) { toast.error("Could not update user"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`Role set to ${role}`);
      fetchUsers();
    } catch (e) { toast.error("Could not change role"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone. Their projects will remain but become unowned.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-users-page">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <Link to="/admin" className="rcw-btn-ghost text-sm mb-3">
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900">Users</h1>
            <p className="mt-1 text-sm text-gray-600">
              Create staff accounts. New users receive a welcome email with a temporary password.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)} className="rcw-btn-amber" data-testid="users-create-btn">
            <UserPlus className="h-4 w-4" /> Add user
          </button>
        </div>

        <div className="rcw-card overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">No users yet.</td></tr>
                )}
                {users.map((u) => {
                  const isSelf = me?.id === u.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{u.name || "—"} {isSelf && <span className="text-xs text-gray-400">(you)</span>}</td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          disabled={isSelf}
                          className="rcw-input text-xs py-1"
                          data-testid={`user-role-${u.email}`}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {u.must_change_password ? (
                          <span className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700">Awaiting first sign-in</span>
                        ) : u.disabled ? (
                          <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-700">Disabled</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => resetPw(u)} className="rcw-btn-ghost text-xs" data-testid={`user-reset-${u.email}`} title="Reset password">
                            <RefreshCw className="h-3.5 w-3.5" /> Reset
                          </button>
                          {!isSelf && (
                            <>
                              <button onClick={() => toggleDisable(u)} className="rcw-btn-ghost text-xs" data-testid={`user-toggle-${u.email}`}>
                                {u.disabled ? <><UserCheck className="h-3.5 w-3.5" /> Enable</> : <><UserMinus className="h-3.5 w-3.5" /> Disable</>}
                              </button>
                              <button onClick={() => removeUser(u)} className="rcw-btn-ghost text-xs text-red-600 hover:bg-red-50" data-testid={`user-delete-${u.email}`}>
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={(u) => { setTempPwReveal({ email: u.email, temp_password: u.temp_password }); fetchUsers(); }} />}
      {tempPwReveal && <TempPwModal data={tempPwReveal} onClose={() => setTempPwReveal(null)} />}
    </Layout>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", { email: email.trim(), name: name.trim(), role });
      toast.success(`Invite sent to ${data.email}`);
      onCreated(data);
      onClose();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not create user");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add a new user">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required data-testid="create-user-email" />
        </Field>
        <Field label="Full name">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-user-name" placeholder="e.g. Sam Davies" />
        </Field>
        <Field label="Role" required>
          <Select value={role} onChange={(e) => setRole(e.target.value)} options={["user", "admin"]} data-testid="create-user-role" />
        </Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="text-xs text-gray-500">
          A temporary password will be generated and emailed to the user. They'll be asked to change it on first sign-in.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rcw-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="rcw-btn-amber" data-testid="create-user-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create user
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TempPwModal({ data, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(data.temp_password);
    toast.success("Copied to clipboard");
  };
  return (
    <Modal onClose={onClose} title="Temporary password">
      <p className="text-sm text-gray-700">
        We've emailed <b>{data.email}</b> a welcome message with this temporary password.
        You can also share it directly:
      </p>
      <div className="mt-4 flex items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono break-all" data-testid="temp-pw-value">
          {data.temp_password}
        </code>
        <button onClick={copy} className="rcw-btn-ghost" data-testid="temp-pw-copy">
          <Copy className="h-4 w-4" /> Copy
        </button>
      </div>
      <div className="mt-4 text-xs text-gray-500">
        For security, this password is only shown once. They'll be asked to change it on first sign-in.
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={onClose} className="rcw-btn-primary">Done</button>
      </div>
    </Modal>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 sm:p-7">
        <div className="flex items-center justify-between mb-4">
          <div className="rcw-label">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
